import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Orb } from '../components/Orb';
import { GlassPanel } from '../components/GlassPanel';
export type Accent = 'violet' | 'teal' | 'ember';
const ACCENTS: Record<Accent, {
  hue: number;
  rgb: string;
}> = {
  violet: {
    hue: 0,
    rgb: '167,120,255'
  },
  teal: {
    hue: 145,
    rgb: '80,220,205'
  },
  ember: {
    hue: 255,
    rgb: '255,168,110'
  }
};
const spring = {
  smooth: {
    type: 'spring',
    stiffness: 260,
    damping: 26,
    mass: 1
  },
  snappy: {
    type: 'spring',
    stiffness: 500,
    damping: 30,
    mass: 0.8
  }
} as const;
interface ComingSoonProps {
  accent: Accent;
  orbScale: number;
}
export function ComingSoon({
  accent,
  orbScale
}: ComingSoonProps) {
  const reduce = useReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: stageRef,
    offset: ['start end', 'end start']
  });
  const driftRaw = useTransform(scrollYProgress, [0, 1], [24, -24]);
  const drift = reduce ? 0 : driftRaw;
  const {
    hue,
    rgb
  } = ACCENTS[accent];
  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.06,
        delayChildren: 0.12
      }
    }
  };
  const item = reduce ? {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        duration: 0.15
      }
    }
  } : {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: spring.smooth
    }
  };
  return <main className="relative flex min-h-full w-full flex-col overflow-hidden bg-ink font-sans text-white" data-id="element-6" data-mp="1g4m241q4q0vt">
      {/* Background stage */}
      <div ref={stageRef} className="pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-7" data-mp="1puxi4mn380j2">
        <motion.div style={{
        y: drift,
        width: `${orbScale}vmax`,
        height: `${orbScale}vmax`
      }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 will-change-transform" data-id="element-8" data-mp="1tw0x76xb9sj8">
          {reduce ? <div className="h-full w-full rounded-full opacity-40 blur-3xl" style={{
          backgroundColor: `rgba(${rgb},0.35)`
        }} data-id="element-9" data-mp="23v8bbwbvdr7e" /> : <Orb hue={hue} hoverIntensity={0.55} rotateOnHover forceHoverState={false} data-id="element-10" data-mp="3uz9wjo79jv7v" />}
        </motion.div>
        <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_50%,transparent_35%,rgba(5,6,10,0.82)_100%)]" data-id="element-11" data-mp="dis7gu6b5blb" />
      </div>

      <header className="relative z-10 flex items-center justify-between border-b border-white/[0.07] px-6 py-5 md:px-10" data-id="element-12" data-mp="8pgz2olukmra">
        <div className="flex items-center gap-3" data-id="element-13" data-mp="ihmpvv0d4y4w">
          <span className="grid h-8 w-8 place-items-center rounded-sm border border-white/15 bg-white/[0.06] backdrop-blur-md" aria-hidden="true" data-id="element-14" data-mp="igtu1p2xzt1j">
            <span className="h-2.5 w-2.5 rounded-full" style={{
            backgroundColor: `rgb(${rgb})`
          }} data-id="element-15" data-mp="rj2z3zffyu7j" />
          </span>
          <span className="text-[15px] font-semibold tracking-[-0.01em]" data-id="element-16" data-mp="2y8hmp8axazej">Pebble Solutions</span>
        </div>
        <span className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3.5 py-1.5 font-mono text-[11px] text-white/70 backdrop-blur-md" data-id="element-17" data-mp="2tb95pb1ih1ev">
          <span className="h-1.5 w-1.5 rounded-full" style={{
          backgroundColor: `rgb(${rgb})`
        }} data-id="element-18" data-mp="1dovscutnkzbp" />
          In development
        </span>
      </header>

      <div className="relative z-10 flex flex-1 items-center justify-center px-5 py-16 md:px-10 md:py-24" data-id="element-19" data-mp="14042qtc2er3y">
        <GlassPanel className="w-full max-w-[900px] px-7 py-12 md:px-16 md:py-16" data-id="element-20" data-mp="q0w35zbzpgz8">
          <motion.div variants={container} initial="hidden" animate="show" data-id="element-21" data-mp="uzph1sv6v9zx">
            <motion.h1 variants={item} className="max-w-[20ch] text-[clamp(38px,6.2vw,84px)] font-bold leading-[1.02] tracking-[-0.025em]" data-id="element-22" data-mp="uxmek9cunoeb">
              Every big move starts with a small stone.
            </motion.h1>

            <motion.p variants={item} className="mt-7 max-w-[62ch] text-[17px] leading-[1.65] text-white/65 md:text-[18px]" data-id="element-23" data-mp="2ahiw3ej8sxl9">
              We're building a calmer place for teams to lay out how their business actually
              works — and to move the pieces around together, in real time, on one living
              surface. It arrives soon.
            </motion.p>

            <motion.div variants={item} className="mt-11 flex flex-col gap-5 border-t border-white/10 pt-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-24" data-mp="6bbmzg6zfk99">
              <div data-id="element-25" data-mp="3skmir3myvmaj">
                <p className="font-mono text-[11px] text-white/40" data-id="element-26" data-mp="3sl4dtwxw7ugv">Talk to us</p>
                <motion.a href="mailto:info@pebble.solutions" whileHover={reduce ? undefined : {
                scale: 1.02
              }} whileTap={reduce ? undefined : {
                scale: 0.97
              }} transition={spring.snappy} className="mt-2 inline-flex items-center gap-2 text-[19px] font-semibold tracking-[-0.01em] text-white transition-colors duration-150 ease-out hover:text-white/80 md:text-[22px]" data-id="element-27" data-mp="3nsbandi2ou6w">
                  info@pebble.solutions
                  <ArrowUpRightIcon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" data-id="element-28" data-mp="2uq236dzf6pl0" />
                </motion.a>
              </div>
              <p className="max-w-[26ch] text-[14px] leading-[1.5] text-white/40" data-id="element-29" data-mp="34nycr1cplch0">
                Early access is going out to a first handful of teams.
              </p>
            </motion.div>
          </motion.div>
        </GlassPanel>
      </div>

      <footer className="relative z-10 flex items-center justify-between border-t border-white/[0.07] px-6 py-5 font-mono text-[11px] text-white/35 md:px-10" data-id="element-30" data-mp="2c1na7l5jl1hk">
        <span data-id="element-31" data-mp="2lxqaelgu2q1v">© {new Date().getFullYear()} Pebble Solutions</span>
        <span data-id="element-32" data-mp="2qoq7prtv7a4w">Coming soon</span>
      </footer>
    </main>;
}